// Library Methods
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

// Hooks
import useTokenAccess from "src/hooks/auth/useTokenAccess";
import { useSnackbarContext } from "src/contexts/alerts/SnackbarContext";

// Utils
import { getEMRErrorMessage } from "src/utils/emr/emrErrorHelper";

// Types and API
import {
  EMRHubConnectionSourceKind,
  EMRHubConnectionDirection,
} from "../../types";
import { createEMRHubConnection } from "../../api";

type CreateEMRHubConnectionPayload = {
  clinicId: string;
  name: string;
  sourceKind: EMRHubConnectionSourceKind;
  direction: EMRHubConnectionDirection;
  configJson: Record<string, unknown>;
  enabled: boolean;
  deviceKey?: null | string;
};

export const useCreateEMRHubConnection = (
  clinicId: string | undefined,
  hubId: string | undefined
) => {
  const queryClient = useQueryClient();
  const { getToken } = useTokenAccess();
  const { t } = useTranslation();
  const { setSnackbarInfo } = useSnackbarContext();

  return useMutation({
    mutationFn: async (payload: CreateEMRHubConnectionPayload) => {
      if (!clinicId || !hubId) throw new Error();

      try {
        const token = await getToken();
        return createEMRHubConnection(token, clinicId, hubId, payload);
      } catch (error) {
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["emr-hub-connections", clinicId, hubId],
      });
      setSnackbarInfo({
        variant: "success",
        message: t("Connection created successfully!"),
      });
    },
    onError: (error) => {
      setSnackbarInfo({
        variant: "error",
        message: getEMRErrorMessage(
          error,
          t("Connection could not be created!"),
          t
        ),
      });
    },
  });
};
